import getFirebase from './firebase';

const firebase = getFirebase();
const db = firebase.firestore();

/**
 * User Authority Service
 * Centralized service for handling user authentication, authorization, and authority data
 */
export const UserAuthorityService = {
  /**
   * Get current authenticated user
   * @returns {Promise<Object|null>} Current Firebase user or null if not authenticated
   */
  getCurrentUser: () => {
    return new Promise(resolve => {
      const unsubscribe = firebase.auth().onAuthStateChanged(user => {
        unsubscribe();
        resolve(user);
      });
    });
  },

  /**
   * Get current user's ID token
   * @returns {Promise<string|null>} User's ID token or null if not authenticated
   */
  getCurrentUserToken: async () => {
    const user = await UserAuthorityService.getCurrentUser();
    if (user) {
      return await user.getIdToken();
    }
    return null;
  },

  /**
   * Listen to authentication state changes
   * @param {Function} callback - Callback function to handle auth state changes
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChanged: callback => {
    return firebase.auth().onAuthStateChanged(callback);
  },

  /**
   * Get user's authority data from Firestore
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object|null>} User authority data or null if not found
   */
  getUserAuthority: async (uid = null) => {
    try {
      let userId = uid;

      if (!userId) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          return null; // Return null instead of throwing error
        }
        userId = currentUser.uid;
      }

      const doc = await db
        .collection('authority')
        .doc(userId)
        .get();

      if (doc.exists) {
        return {
          uid: userId,
          ...doc.data()
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching user authority:', error);
      return null; // Return null instead of throwing
    }
  },

  /**
   * Get user's member data from Firestore
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object|null>} User member data or null if not found
   */
  getUserMemberData: async (uid = null) => {
    try {
      let userId = uid;

      if (!userId) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }
        userId = currentUser.uid;
      }

      const doc = await db
        .collection('members')
        .doc(userId)
        .get();

      if (doc.exists) {
        return {
          uid: userId,
          ...doc.data()
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching user member data:', error);
      throw error;
    }
  },

  /**
   * Get complete user profile (authority + member data)
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<Object|null>} Complete user profile or null if not found
   */
  getUserProfile: async (uid = null) => {
    try {
      let userId = uid;

      if (!userId) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }
        userId = currentUser.uid;
      }

      const [authorityData, memberData] = await Promise.all([
        UserAuthorityService.getUserAuthority(userId),
        UserAuthorityService.getUserMemberData(userId)
      ]);

      if (!authorityData) {
        return null;
      }

      return {
        ...authorityData,
        memberData: memberData || null
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Check if current user has a specific role
   * @param {string} role - Role to check for
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user has the role
   */
  hasRole: async (role, uid = null) => {
    try {
      const authority = await UserAuthorityService.getUserAuthority(uid);
      if (!authority || !authority.role) {
        return false;
      }

      return Array.isArray(authority.role) ? authority.role.includes(role) : authority.role === role;
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  },

  /**
   * Check if current user has a specific level
   * @param {string} level - Level to check for (e.g., 'ADMIN', 'APPROVED_MEMBER')
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user has the level
   */
  hasLevel: async (level, uid = null) => {
    try {
      // If no uid provided, check if there's a current user
      if (!uid) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          console.log('hasLevel: No authenticated user found');
          return false; // No authenticated user, return false instead of throwing
        }
        uid = currentUser.uid;
      }

      console.log(`hasLevel: Checking level "${level}" for user:`, uid);
      const authority = await UserAuthorityService.getUserAuthority(uid);
      console.log('hasLevel: User authority data:', authority);

      if (!authority || !authority.level) {
        console.log('hasLevel: No authority data or level found');
        return false;
      }

      const hasLevel = authority.level === level;
      console.log(`hasLevel: User has level "${level}":`, hasLevel);
      return hasLevel;
    } catch (error) {
      console.error('Error checking user level:', error);
      return false;
    }
  },

  /**
   * Check if current user is admin
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user is admin
   */
  isAdmin: async (uid = null) => {
    return await UserAuthorityService.hasLevel('ADMIN', uid);
  },

  /**
   * Check if current user is super admin
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user is super admin
   */
  isSuperAdmin: async (uid = null) => {
    return await UserAuthorityService.hasRole('SUPERADMIN', uid);
  },

  /**
   * Check if current user is an approved member
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user is an approved member
   */
  isApprovedMember: async (uid = null) => {
    return await UserAuthorityService.hasLevel('APPROVED_MEMBER', uid);
  },

  /**
   * Check if current user can approve
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user can approve
   */
  canApprove: async (uid = null) => {
    return await UserAuthorityService.hasRole('APPROVAL', uid);
  },

  /**
   * Check if current user is chairman
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user is chairman
   */
  isChairman: async (uid = null) => {
    return await UserAuthorityService.hasRole('CHAIRMAN', uid);
  },

  /**
   * Check if current user is deleted
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<boolean>} True if user is deleted
   */
  isDeleted: async (uid = null) => {
    return await UserAuthorityService.hasLevel('DELETED', uid);
  },

  /**
   * Update user authority data
   * @param {Object} data - Authority data to update
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<void>}
   */
  updateUserAuthority: async (data, uid = null) => {
    try {
      let userId = uid;

      if (!userId) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }
        userId = currentUser.uid;
      }

      await db
        .collection('authority')
        .doc(userId)
        .set(data, { merge: true });
    } catch (error) {
      console.error('Error updating user authority:', error);
      throw error;
    }
  },

  /**
   * Update user member data
   * @param {Object} data - Member data to update
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<void>}
   */
  updateUserMemberData: async (data, uid = null) => {
    try {
      let userId = uid;

      if (!userId) {
        const currentUser = await UserAuthorityService.getCurrentUser();
        if (!currentUser) {
          throw new Error('No authenticated user found');
        }
        userId = currentUser.uid;
      }

      await db
        .collection('members')
        .doc(userId)
        .set(data, { merge: true });
    } catch (error) {
      console.error('Error updating user member data:', error);
      throw error;
    }
  },

  /**
   * Sign out current user
   * @returns {Promise<void>}
   */
  signOut: async () => {
    try {
      sessionStorage.clear();
      await firebase.auth().signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  /**
   * Get user's display name
   * @param {string} uid - User ID (optional, uses current user if not provided)
   * @returns {Promise<string>} User's display name
   */
  getUserDisplayName: async (uid = null) => {
    try {
      const authority = await UserAuthorityService.getUserAuthority(uid);
      if (!authority) {
        return 'Unknown User';
      }

      const name = authority.name || '';
      const surname = authority.surname || '';

      return `${name} ${surname}`.trim() || authority.email || 'Unknown User';
    } catch (error) {
      console.error('Error getting user display name:', error);
      return 'Unknown User';
    }
  },

  /**
   * Check if user's email is verified
   * @returns {Promise<boolean>} True if email is verified
   */
  isEmailVerified: async () => {
    try {
      const currentUser = await UserAuthorityService.getCurrentUser();
      return currentUser ? currentUser.emailVerified : false;
    } catch (error) {
      console.error('Error checking email verification:', error);
      return false;
    }
  },

  /**
   * Middleware function to check if current user is deleted and sign them out
   * This can be used in components that need to verify user isn't deleted
   * @param {Function} onDeleted - Optional callback when user is deleted
   * @returns {Promise<boolean>} True if user is valid (not deleted), false if deleted
   */
  validateUserNotDeleted: async (onDeleted = null) => {
    try {
      const isDeleted = await UserAuthorityService.isDeleted();

      if (isDeleted) {
        await UserAuthorityService.signOut();

        if (onDeleted && typeof onDeleted === 'function') {
          onDeleted();
        }

        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating user status:', error);
      return true; // Assume valid if we can't check
    }
  }
};

export default UserAuthorityService;
